<template>
	<div class="h-full overflow-y-auto bg-white dark:bg-gray-700" :class="{'non-apple-scrollbars': isNoAppleDevice}">
		<AppError v-if="userDataLoadingFailed" />
		<BaseCenteredPage v-else-if="loading || navigationTrigger">
			<BaseIcon name="mono/load" class="my-auto w-24 h-24" />
		</BaseCenteredPage>
		<template v-else>
			<RouterView class="flex-grow min-w-0" />
			<Modals v-if="$route.query.modal" />
		</template>
		<BirdNotify />
	</div>
</template>

<script>
import AppError from 'shared/views/AppError.vue'
import Modals from 'shared/components/Modals.vue'
import BirdNotify from 'shared/components/BirdNotify.vue'
import colorTheme from 'shared/mixins/colorTheme'
import {useWorkspaceSubscriptionStore} from 'shared/stores/workspaceSubscription'
import {useExperimentStore} from 'shared/stores/experiments'
import {useBrowserInfo} from 'shared/composables/useBrowserInfo'
import appDataLoader from 'shared/mixins/appDataLoader'
import isDateAfterBugCaptureRelease from 'shared/utils/isDateAfterBugCaptureRelease'
import {BROWSERSTACK_BUG_CAPTURE_PRICING_URL} from 'shared/utils/browserstackUrls'

export default {
	components: {
		AppError,
		Modals,
		BirdNotify,
	},
	mixins: [appDataLoader, colorTheme],
	setup() {
		return {
			workspaceSubscription: useWorkspaceSubscriptionStore(),
			browserInfo: useBrowserInfo(),
			experimentStore: useExperimentStore(),
		}
	},
	data() {
		return {
			activeSubscriptionNotification: null,
		}
	},
	computed: {
		isNoAppleDevice() {
			return this.browserInfo?.platform?.vendor !== 'Apple'
		},
		route() {
			return this.$route?.matched?.[0]
		},
		routeParams() {
			return this.$route.params
		},
		routeError() {
			return this.$route?.query?.error
		},
		loading() {
			if (this.$auth.loading.value) return true
			if (this.$route?.meta?.waitForUserData && (this.$store.loadingUser || this.$store.loadingUserWorkspaces)) {
				return true
			}
			if (this.$route?.meta?.waitForWorkspaceData && this.$store.loadingCurrentWorkspace) {
				return true
			}

			if (this.$route?.meta?.requiresExperimentData && !this.experimentStore.experimentsLoadedForUser) {
				return true
			}
			return false
		},
		// a single navigation trigger which is used to redirect to the correct route
		navigationTrigger() {
			if (['unauthorized', 'access_denied'].includes(this.routeError) && this.$route?.name !== 'auth-error') {
				return 'unauthorized'
			}

			// we can always wait for $auth to load
			if (this.$auth.loading.value) return

			// if user needs to login and is not authenticated, trigger redirect
			if (this.$route?.meta?.requiresLogin && !this.$auth.isAuthenticated.value) return 'login'

			// all checks below require a user to be loaded
			if (this.$store.loadingUser) return

			// if a user is null, it was not found in the DB
			if (this.$store.user === null) return 'userNotFound'

			if (this.$store.user.forceLogout) return 'forceLogout'

			// If user has not accepted the BrowserStack ToS, redirect to the Bug Capture landing page
			if (
				this.$route?.name !== 'auth-post-login' &&
				this.$route?.meta?.requiresSignupCompletion &&
				this.$store.isBrowserstackUser &&
				this.$store.user.browserstackProductAccess &&
				!this.$store.user.browserstackTosAccepted
			) {
				return 'browserstackTos'
			}

			// if user is in an invalid workspace, redirect to accept invite or trigger switch
			if (this.$store.loadingUserWorkspaces || this.$store.loadingCurrentWorkspace) return

			// if user is a BrowserStack Live user but does not have access to the product they should be redirected to the waitlist page
			// otherwise, we redirect to the pricing page (for all other non-Live BrowserStack users)
			// We only do this if the route is not auth-post-login, because this redirection logic is also present there, and is here as a safeguard
			// in case the user tries to access the product using direct URLs
			if (
				this.$route?.name !== 'workspace-request-access' &&
				this.$route?.name !== 'auth-post-login' &&
				this.$route?.meta?.requiresLogin &&
				this.$store.isBrowserstackWorkspace &&
				!this.$store.user.browserstackProductAccess
			) {
				if (
					(this.$workspaceSubscription.isTrialPlan && !this.$workspaceSubscription.hasTrialEnded) ||
					(this.$workspaceSubscription.plan?.id === 'browserstack-free-plus' &&
						this.$workspaceSubscription.isActive)
				) {
					return 'workspace-request-access'
				}
				return 'pricingPage'
			}

			if (this.isUnauthorizedWorkspace) {
				if (this.$route?.name !== 'workspace-delete' && this.routeParams.workspaceId) {
					return 'checkForInvitesOrDomainAutojoin'
				}
				return 'switchWorkspace'
			}

			// if route requires signup completion or consent
			if (
				(this.$route?.meta?.requiresUserConsent && !this.$store.hasUserGivenConsent) ||
				(this.$route?.meta?.requiresSignupCompletion && !this.$store.hasUserCompletedSignup)
			) {
				return 'completeSignup'
			}

			// if route requires user to have a workspace and they have none
			if (this.$route?.meta?.requiresWorkspace && !this.$store.workspaces.length) {
				return 'createWorkspace'
			}

			const userSignedUpBeforeBugCaptureRelease =
				this.$store.user.createdAt && !isDateAfterBugCaptureRelease(new Date(this.$store.user.createdAt))
			const userQualifiesForAcquisitionNotice =
				userSignedUpBeforeBugCaptureRelease &&
				!this.$store.isBrowserstackUser &&
				!this.$store.user.sawAcquisitionNoticeAt
			const routeQualifiesForAcquisitionNotice =
				this.$route?.path !== '/' &&
				this.$route?.name !== 'workspace' &&
				!this.$route?.path?.includes('/complete-signup') &&
				!this.$route?.path?.includes('/auth') &&
				!this.$route?.path?.includes('/settings') &&
				!this.$route?.query?.modal
			if (userQualifiesForAcquisitionNotice && routeQualifiesForAcquisitionNotice) {
				return 'showAcquisitionNotice'
			}

			return undefined
		},
		userDataLoadingFailed() {
			if (this.$auth.loading.value) return false
			if (!this.$route?.meta?.requiresLogin) return false
			if (this.$store.loadingUser) return false
			if (this.$store.user?.id) return false
			if (this.navigationTrigger) return false
			return true
		},
		isUnauthorizedWorkspace() {
			if (this.loading) return false
			// 1. if the user is on a session page, redirect is dealt with in `Session.vue`
			if (this.routeParams.sessionId) return false
			// 2. if the route does not require a workspace (eg. accept-invite), just leave it
			if (!this.$route?.meta?.requiresWorkspace) return false
			// 4. all other pages that include `workspaceId` the user should be part of the workspace
			if (!this.routeParams.workspaceId) return false
			if (this.$store.isUserWorkspaceMember) return false

			return true
		},
		// check if we need to warn the user about their workspace's subscription status
		subscriptionNotificationKey() {
			// if in a Session route, wait for session to load (so that session.workspaceId is available)
			if (this.$route.name === 'workspace-subscription-plan-settings') return
			// don't show notification if user lands on the extend-trial route
			if (this.$route.name === 'workspace-extend-trial') return
			// don't show expiration notifications when anonymous users visit public routes
			if (!this.$store.user?.id || !this.$store.workspace?.id) return
			if (this.workspaceSubscription.trialDaysLeft > 0 && this.workspaceSubscription.trialDaysLeft <= 5) {
				return 'trial-ending'
			}
			if (this.workspaceSubscription.problem === 'free-plan-ended' && this.$store.isBrowserstackWorkspace) {
				return
			}
			return this.workspaceSubscription.problem
		},
	},
	watch: {
		navigationTrigger: {
			immediate: true,
			async handler(navigationTrigger) {
				if (import.meta.env.MODE !== 'production') {
					//eslint-disable-next-line no-console
					console.log('%c[App.vue] navigationTrigger: ', 'color: green', navigationTrigger)
				}
				if (!navigationTrigger) return
				switch (navigationTrigger) {
					case 'unauthorized':
						await this.$routerHelpers.push({path: '/auth/error', query: this.$route.query})
						break
					case 'browserstackTos':
						await this.$routerHelpers.push('/complete-signup/get-started')
						break
					case 'forceLogout':
						await this.$auth.logout({force: true})
						break
					case 'login':
						this.$auth.login({
							redirectAfterLogin: this.$route.fullPath,
						})
						break
					case 'workspace-request-access':
						await this.$routerHelpers.push(`/workspaces/${this.$store.workspace?.id}/request-access`)
						break
					case 'pricingPage':
						await this.redirectToBrowserstack(BROWSERSTACK_BUG_CAPTURE_PRICING_URL)
						break
					case 'completeSignup':
					case 'completeSignupConsent':
						this.$routerHelpers.navigateToCompleteSignup()
						break
					case 'createWorkspace':
						await this.$routerHelpers.push('/workspaces/create')
						break
					case 'switchWorkspace':
						await this.$routerHelpers.push('/')
						break
					case 'checkForInvitesOrDomainAutojoin':
						if (sessionStorage) sessionStorage.redirectUrl = this.$route.fullPath
						await this.$routerHelpers.push(`/workspaces/${this.routeParams.workspaceId}/join`)
						break
					case 'showAcquisitionNotice':
						this.$modals.open('acquisition-notice')
						break
					case 'userNotFound':
						await this.$routerHelpers.replace('/auth/error?error_description=user.not_found')
						break
				}
			},
		},
		// warn the user about their workspace's subscription status
		subscriptionNotificationKey(notificationKey) {
			if (this.activeSubscriptionNotification) {
				this.$notify.remove(this.activeSubscriptionNotification.id)
			}
			if (!notificationKey) return
			const userNotificationKey = `${notificationKey}-notification-dismissed.${this.$store.user.id}.${this.$store.workspace.id}`
			if (localStorage?.[userNotificationKey]) return
			const notification = {}
			switch (notificationKey) {
				case 'free-plan-ended':
					notification.title = 'Free plans are no longer supported'
					notification.message = `Upgrade now to keep using ${this.$store.productName}.`
					break
				case 'subscription-ended':
					notification.title = 'Your subscription has ended'
					notification.message = 'Please restart your plan to continue reporting bugs again.'
					break
				case 'subscription-paused':
					notification.title = 'Your subscription has been paused'
					notification.message = 'Please update your payment details to resume your plan.'
					break
				case 'trial-ending':
					notification.title = 'Your trial is ending soon'
					notification.message = `<span class="mb-2 block">Upgrade now to continue using ${this.$store.productName} and enjoy faster bug reporting with:</span><ul class="list-disc space-y-2 pl-12"><li>Integrations with your issue tracker</li><li>Workflow automations</li><li>E2E test templates</li><li>Advanced team management roles</li></ul>`
					break
				case 'trial-ended':
					notification.title = 'Your trial has ended'
					notification.message = `<span class="mb-2 block">Upgrade now to continue using ${this.$store.productName} and enjoy faster bug reporting with:</span><ul class="list-disc space-y-2 pl-12"><li>Integrations with your issue tracker</li><li>Workflow automations</li><li>E2E test templates</li><li>Advanced team management roles</li></ul>`
					break
			}
			this.activeSubscriptionNotification = this.$notify.warn({
				...notification,
				dangerouslyUseHTMLString: true,
				duration: 0,
				onClick: () => {
					if (!this.hasUserBugReporterRole) {
						this.$routerHelpers.navigateToWorkspaceSettings('subscription-plan')
					}
				},
				onClose: () => {
					if (localStorage) localStorage[userNotificationKey] = true
				},
			})
		},
	},
	async mounted() {
		// Unregister existing service workers
		if (!navigator.serviceWorker) return
		const registrations = await navigator.serviceWorker.getRegistrations()
		for (const registration of registrations) {
			registration.unregister()
		}
	},
	methods: {
		async redirectToBrowserstack(url) {
			// if the user has a legacy workspace, move them to that workspace in case
			// they do not have access to bug-capture, and open the URL in a new tab
			// otherwise, open the URL in the current tab, making the user leave the app
			const switchedToLegacyWorkspace = await this.switchToLegacyWorkspaceIfPresent()
			const target = switchedToLegacyWorkspace ? '_blank' : '_self'
			window.open(url, target)
		},
		async switchToLegacyWorkspaceIfPresent() {
			if (!this.$store.legacyWorkspaces.length) return false
			await this.$routerHelpers.push(`/workspaces/${this.$store.legacyWorkspaces[0].workspace.id}`)
			return true
		},
	},
}
</script>
